export const LOOKING_FOR_OPTIONS = [
  { value: 'adventures', label: 'Activities' },
  { value: 'community', label: 'Community' },
  { value: 'support', label: 'Support' },
  { value: 'connect', label: 'Just to connect' },
] as const;

export const PROFILE_INTEREST_OPTIONS = [
  { value: 'outdoors', label: 'Outdoors' },
  { value: 'creative', label: 'Creative play' },
  { value: 'coffee-chat', label: 'Coffee & chat' },
  { value: 'low-cost', label: 'Low-cost days out' },
  { value: 'school-holidays', label: 'School holidays' },
  { value: 'support-chats', label: 'Support chats' },
] as const;

export const PROFILE_AVAILABILITY_OPTIONS = [
  { value: 'weekday-mornings', label: 'Weekday mornings' },
  { value: 'weekday-afternoons', label: 'Weekday afternoons' },
  { value: 'weekday-evenings', label: 'Weekday evenings' },
  { value: 'weekends', label: 'Weekends' },
  { value: 'school-holidays', label: 'School holidays' },
] as const;

export const PROFILE_CUSTODY_OPTIONS = [
  { value: 'full-time', label: 'Full-time with me' },
  { value: 'shared-care', label: 'Shared care' },
  { value: 'varies', label: 'Varies week to week' },
  { value: 'prefer-not-to-say', label: 'Prefer not to say' },
] as const;

export const DISCOVERY_CHILD_AGE_OPTIONS = [
  { value: '0-4', label: '0–4' },
  { value: '5-8', label: '5–8' },
  { value: '9-12', label: '9–12' },
  { value: '13+', label: '13+' },
] as const;

export type LookingForValue = (typeof LOOKING_FOR_OPTIONS)[number]['value'];
export type ProfileInterestValue = (typeof PROFILE_INTEREST_OPTIONS)[number]['value'];
export type ProfileAvailabilityValue = (typeof PROFILE_AVAILABILITY_OPTIONS)[number]['value'];
export type ProfileCustodyValue = (typeof PROFILE_CUSTODY_OPTIONS)[number]['value'];
export type DiscoveryChildAgeValue = (typeof DISCOVERY_CHILD_AGE_OPTIONS)[number]['value'];

export type MemberDiscoveryFilters = {
  childAge: DiscoveryChildAgeValue | 'all';
  interests: ProfileInterestValue[];
  availability: ProfileAvailabilityValue[];
};

export type DiscoverableMember = {
  display_name?: string | null;
  interests?: string[] | null;
  availability?: string[] | null;
  child_age_bands?: Array<DiscoveryChildAgeValue | string> | null;
  child_birth_years?: number[] | null;
};

const buildLabelMap = <T extends ReadonlyArray<{ value: string; label: string }>>(options: T) =>
  Object.fromEntries(options.map((option) => [option.value, option.label] as const));

export const LOOKING_FOR_LABELS = buildLabelMap(LOOKING_FOR_OPTIONS);
export const PROFILE_INTEREST_LABELS = buildLabelMap(PROFILE_INTEREST_OPTIONS);
export const PROFILE_AVAILABILITY_LABELS = buildLabelMap(PROFILE_AVAILABILITY_OPTIONS);
export const PROFILE_CUSTODY_LABELS = buildLabelMap(PROFILE_CUSTODY_OPTIONS);
export const DISCOVERY_CHILD_AGE_LABELS = buildLabelMap(DISCOVERY_CHILD_AGE_OPTIONS);

const DISCOVERY_CHILD_AGE_SET = new Set<DiscoveryChildAgeValue>(
  DISCOVERY_CHILD_AGE_OPTIONS.map((option) => option.value),
);

const getAgeBandForAge = (age: number): DiscoveryChildAgeValue | null => {
  if (age < 0) return null;
  if (age <= 4) return '0-4';
  if (age <= 8) return '5-8';
  if (age <= 12) return '9-12';
  return '13+';
};

export const getDiscoveryAgeBands = (
  values: Array<number | string> | null | undefined,
  currentYear = new Date().getFullYear(),
) => {
  const bands = new Set<DiscoveryChildAgeValue>();

  for (const value of values ?? []) {
    if (typeof value === 'string' && DISCOVERY_CHILD_AGE_SET.has(value as DiscoveryChildAgeValue)) {
      bands.add(value as DiscoveryChildAgeValue);
      continue;
    }

    if (typeof value === 'number' && Number.isFinite(value)) {
      const band = getAgeBandForAge(currentYear - value);
      if (band) {
        bands.add(band);
      }
    }
  }

  return Array.from(bands);
};

const overlaps = (selected: string[], current: string[] | null | undefined) => {
  if (!selected.length) return true;
  if (!current?.length) return false;
  return selected.some((value) => current.includes(value));
};

export const matchesMemberDiscoveryFilters = (
  member: DiscoverableMember,
  filters: MemberDiscoveryFilters,
  currentYear = new Date().getFullYear(),
) => {
  const ageBands = getDiscoveryAgeBands(member.child_age_bands ?? member.child_birth_years, currentYear);
  const matchesAge = filters.childAge === 'all' ? true : ageBands.includes(filters.childAge);
  const matchesInterests = overlaps(filters.interests, member.interests);
  const matchesAvailability = overlaps(filters.availability, member.availability);

  return matchesAge && matchesInterests && matchesAvailability;
};
